import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { useComplianceAnalysis } from '../../../hooks/useComplianceAnalysis';

const RegulatoryLookup = ({ onRuleSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedRule, setExpandedRule] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [ruleExplanation, setRuleExplanation] = useState(null);

  const { isAnalyzing, explainRule } = useComplianceAnalysis();

  const regulatoryRules = [
    {
      id: 'fdic-001',
      title: 'FDIC Advertising Guidelines',
      category: 'Banking',
      regulation: 'FDIC Part 328',
      description: 'Guidelines for advertising deposit products and FDIC insurance coverage',
      keyPoints: [
        'Must clearly state FDIC insurance coverage limits',
        'Cannot make misleading statements about deposit insurance',
        'Must include proper FDIC member disclaimers'
      ],
      examples: [
        'Deposits are insured by the FDIC up to $250,000 per depositor',
        'Member FDIC - Equal Housing Lender'
      ],
      lastUpdated: '2024-09-15'
    },
    {
      id: 'cfpb-002',
      title: 'Truth in Lending Act (TILA)',
      category: 'Lending',
      regulation: 'CFPB Regulation Z',
      description: 'Requirements for advertising credit products and loan terms',
      keyPoints: [
        'Must disclose APR in credit advertisements',
        'Cannot advertise misleading promotional rates',
        'Must include all material terms and conditions'
      ],
      examples: [
        'APR as low as 3.99% for qualified borrowers',
        'Terms and conditions apply. See website for details.'
      ],
      lastUpdated: '2024-09-10'
    },
    {
      id: 'sec-003',
      title: 'Investment Company Act',
      category: 'Investment',
      regulation: 'SEC Rule 482',
      description: 'Advertising rules for mutual funds and investment products',
      keyPoints: [
        'Must include risk disclosures for investment products',
        'Cannot guarantee investment returns',
        'Must provide balanced presentation of risks and benefits'
      ],
      examples: [
        'Past performance does not guarantee future results',
        'Investments involve risk including potential loss of principal'
      ],
      lastUpdated: '2024-09-08'
    },
    {
      id: 'ftc-004',
      title: 'Fair Credit Reporting Act',
      category: 'Credit',
      regulation: 'FTC FCRA Section 615',
      description: 'Requirements for advertising credit monitoring and reporting services',
      keyPoints: [
        'Must clearly disclose free vs. paid services',
        'Cannot make false claims about credit improvement',
        'Must provide clear cancellation terms'
      ],
      examples: [
        'Free credit score with paid subscription',
        'Cancel anytime with no penalty'
      ],
      lastUpdated: '2024-09-12'
    }
  ];

  const categories = ['All', 'Banking', 'Lending', 'Investment', 'Credit'];

  const filteredRules = regulatoryRules?.filter(rule => {
    const matchesSearch = searchTerm === '' || 
      rule?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      rule?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      rule?.regulation?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    
    const matchesCategory = selectedCategory === 'All' || rule?.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const handleExplainRule = async (regulation, context = '') => {
    const explanation = await explainRule(regulation, context);
    if (explanation) {
      setRuleExplanation(explanation);
    }
  };

  const handleSearchWithAI = async () => {
    if (!searchTerm?.trim()) return;

    // Use AI to find relevant regulations based on search term
    try {
      const searchPrompt = `Find regulations related to: "${searchTerm}"`;
      const explanation = await explainRule(searchTerm, 'regulatory search');
      
      if (explanation) {
        setSearchResults([{
          id: `ai-search-${Date.now()}`,
          title: `AI Search: ${searchTerm}`,
          regulation: explanation?.regulation || searchTerm,
          category: 'AI Search',
          summary: explanation?.overview || 'AI-powered search result',
          details: explanation,
          isAIResult: true
        }]);
      }
    } catch (error) {
      console.error('AI search error:', error);
    }
  };

  const handleToggleExpand = (ruleId) => {
    setExpandedRule(expandedRule === ruleId ? null : ruleId);
  };

  const handleRuleSelect = (rule) => {
    if (onRuleSelect) {
      onRuleSelect(rule);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="h-full flex flex-col">
      {/* Enhanced Search Header */}
      <div className="p-4 border-b border-border">
        <h3 className="font-semibold text-foreground mb-3 flex items-center space-x-2">
          <Icon name="BookOpen" size={20} />
          <span>Regulatory Lookup</span>
          <Icon name="Brain" size={16} className="text-purple-600" />
        </h3>
        
        <div className="space-y-3">
          <div className="flex space-x-2">
            <Input
              type="text"
              placeholder="Search regulations, rules, or compliance topics..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              onKeyPress={(e) => e?.key === 'Enter' && handleSearchWithAI()}
              className="flex-1"
            />
            <Button
              size="sm"
              onClick={handleSearchWithAI}
              disabled={!searchTerm?.trim() || isAnalyzing}
              iconName={isAnalyzing ? "Loader2" : "Brain"}
              iconClassName={isAnalyzing ? "animate-spin" : ""}
              className="bg-purple-600 hover:bg-purple-700"
            >
              AI Search
            </Button>
          </div>

          <div className="flex flex-wrap gap-2">
            {categories?.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-micro ${
                  selectedCategory === category
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Results with AI Enhancement */}
      <div className="flex-1 overflow-y-auto">
        {/* AI Search Results */}
        {searchResults?.length > 0 && (
          <div className="p-4 border-b border-border bg-purple-50">
            <h4 className="font-medium text-purple-800 mb-3 flex items-center space-x-2">
              <Icon name="Sparkles" size={16} />
              <span>AI Search Results</span>
            </h4>
            {searchResults?.map((result) => (
              <div key={result?.id} className="bg-white p-3 rounded-lg border border-purple-200">
                <div className="flex justify-between items-start mb-2">
                  <h5 className="font-medium text-foreground">{result?.title}</h5>
                  {result?.isAIResult && (
                    <span className="text-xs px-2 py-1 bg-purple-100 text-purple-800 rounded-full">
                      AI Powered
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-2">{result?.summary}</p>
                {result?.details && (
                  <div className="space-y-2">
                    {result?.details?.keyRequirements && (
                      <div>
                        <strong className="text-xs">Key Requirements:</strong>
                        <ul className="text-xs list-disc list-inside ml-2">
                          {result?.details?.keyRequirements?.slice(0, 3)?.map((req, idx) => (
                            <li key={idx}>{req}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Regulatory Rules List */}
        {filteredRules?.map((rule) => (
          <div
            key={rule?.id}
            className="border-b border-border last:border-b-0 hover:bg-muted/50 transition-colors"
          >
            <div className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h4 className="font-medium text-foreground">{rule?.title}</h4>
                  <p className="text-sm text-muted-foreground">{rule?.regulation}</p>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <Button
                    size="sm"
                    variant="outline"
                    iconName="Brain"
                    onClick={() => handleExplainRule(rule?.regulation, rule?.summary)}
                    disabled={isAnalyzing}
                    className="text-purple-600 border-purple-200"
                  >
                    AI Explain
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    iconName={expandedRule === rule?.id ? "ChevronUp" : "ChevronDown"}
                    onClick={() => handleToggleExpand(rule?.id)}
                  />
                </div>
              </div>

              <p className="text-sm text-muted-foreground mb-2">{rule?.description}</p>

              <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                <span>Updated: {formatDate(rule?.lastUpdated)}</span>
              </div>
            </div>

            {expandedRule === rule?.id && (
              <div className="border-t border-border p-4 bg-muted/20">
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-foreground mb-2">Key Requirements</h4>
                    <ul className="space-y-1">
                      {rule?.keyPoints?.map((point, index) => (
                        <li key={index} className="flex items-start space-x-2 text-sm text-muted-foreground">
                          <Icon name="Check" size={14} className="text-green-600 mt-0.5 flex-shrink-0" />
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-foreground mb-2">Compliant Examples</h4>
                    <div className="space-y-2">
                      {rule?.examples?.map((example, index) => (
                        <div key={index} className="bg-green-50 border border-green-200 rounded-md p-3">
                          <p className="text-sm text-green-800 font-mono">{example}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Plus"
                      iconPosition="left"
                      onClick={() => handleRuleSelect(rule)}
                    >
                      Apply to Review
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}

        {filteredRules?.length === 0 && (
          <div className="text-center py-8">
            <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-3" />
            <h3 className="text-lg font-medium text-foreground mb-2">No Rules Found</h3>
            <p className="text-muted-foreground">Try adjusting your search terms or category filter.</p>
          </div>
        )}
      </div>
      {/* AI Explanation Panel */}
      {ruleExplanation && (
        <div className="p-4 border-t border-border bg-blue-50">
          <div className="flex justify-between items-start mb-3">
            <h4 className="font-medium text-blue-800 flex items-center space-x-2">
              <Icon name="Brain" size={16} />
              <span>AI Regulatory Explanation</span>
            </h4>
            <Button
              size="sm"
              variant="ghost"
              iconName="X"
              onClick={() => setRuleExplanation(null)}
            />
          </div>

          <div className="space-y-3 text-sm">
            <div>
              <strong>Overview:</strong>
              <p className="mt-1">{ruleExplanation?.overview}</p>
            </div>

            {ruleExplanation?.keyRequirements?.length > 0 && (
              <div>
                <strong>Key Requirements:</strong>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  {ruleExplanation?.keyRequirements?.map((req, idx) => (
                    <li key={idx}>{req}</li>
                  ))}
                </ul>
              </div>
            )}

            {ruleExplanation?.commonViolations?.length > 0 && (
              <div>
                <strong>Common Violations:</strong>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  {ruleExplanation?.commonViolations?.map((violation, idx) => (
                    <li key={idx} className="text-red-700">{violation}</li>
                  ))}
                </ul>
              </div>
            )}

            {ruleExplanation?.bestPractices?.length > 0 && (
              <div>
                <strong>Best Practices:</strong>
                <ul className="list-disc list-inside mt-1 space-y-1">
                  {ruleExplanation?.bestPractices?.map((practice, idx) => (
                    <li key={idx} className="text-green-700">{practice}</li>
                  ))}
                </ul>
              </div>
            )}

            {ruleExplanation?.penalties && (
              <div>
                <strong>Penalties:</strong>
                <p className="mt-1 text-red-700">{ruleExplanation?.penalties}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RegulatoryLookup;